import React, { useState, useEffect, useRef } from 'react';
import './ChillChat.css';

const CHILL_API = process.env.REACT_APP_CHILL_API_URL || 'http://localhost:3031';
const OPENING_MESSAGE = "What are you in the mood to discover tonight?";

const ChannelRecommendation = ({ channel, onPlay }) => {
  const imageId =
    channel?.image?.spotlight_poster ||
    channel?.image?.poster ||
    channel?.spotlight_poster ||
    channel?.poster;

  const imageUrl = imageId
    ? `https://dprodspmedia.cachefly.net/images/${imageId}/480/270?webp=1`
    : null;

  return (
    <div className="chill-recommendation" onClick={() => onPlay(channel)}>
      {imageUrl && (
        <div className="chill-rec-image">
          <img src={imageUrl} alt={channel.title} />
          <div className="chill-rec-play">▶</div>
        </div>
      )}
      <div className="chill-rec-title">{channel.title || channel.name}</div>
    </div>
  );
};

const ChillChat = ({ onChannelSelect, onDismiss }) => {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: OPENING_MESSAGE }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [channels, setChannels] = useState([]);
  const inputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, channels]);

  const sendMessage = async (text) => {
    if (!text.trim() || loading) return;

    const userMsg = { role: 'user', content: text };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);
    setChannels([]);

    try {
      const res = await fetch(`${CHILL_API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          history: messages
        })
      });

      const data = await res.json();
      setMessages([...updatedMessages, { role: 'assistant', content: data.reply }]);
      if (data.channels && data.channels.length > 0) {
        setChannels(data.channels);
      }
    } catch (err) {
      setMessages([...updatedMessages, {
        role: 'assistant',
        content: "Something shifted. Try again in a moment."
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  return (
    <div className="chill-overlay">
      <div className="chill-container">
        {/* Header */}
        <div className="chill-header">
          <div className="chill-brand">
            <span className="chill-dot">●</span>
            <span className="chill-name">chill</span>
          </div>
          <button className="chill-dismiss" onClick={onDismiss}>
            browse catalog →
          </button>
        </div>

        {/* Messages */}
        <div className="chill-messages">
          {messages.map((msg, i) => (
            <div key={i} className={`chill-msg chill-msg--${msg.role}`}>
              {msg.content}
            </div>
          ))}

          {loading && (
            <div className="chill-msg chill-msg--assistant chill-thinking">
              <span>.</span><span>.</span><span>.</span>
            </div>
          )}

          {channels.length > 0 && (
            <div className="chill-recommendations">
              {channels.map((ch, i) => (
                <ChannelRecommendation
                  key={ch._id || i}
                  channel={ch}
                  onPlay={onChannelSelect}
                />
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="chill-input-wrap">
          <input
            ref={inputRef}
            className="chill-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="tell chill what you're looking for..."
            disabled={loading}
          />
          <button
            className="chill-send"
            onClick={() => sendMessage(input)}
            disabled={loading || !input.trim()}
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChillChat;
